import {
  Box,
  Grid,
  TextField,
  Typography,
  Switch,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TableBody,
  TableContainer,
  TableRow,
  TableCell,
  TableHead,
  Input,
  Button,
  IconButton,
  Radio,
  FormControl,
  Select,
  InputLabel,
  MenuItem,
  Autocomplete,
  Alert,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { StyledTextarea } from "../../common/Textareas";
import InfoTip from "../../common/InfoTip";
import { axiosInstance } from "../../../utils/networking";
import cities from "../../../assets/city";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "1px",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  // hide last border

  "td, th": {
    border: 0,
  },
}));

const availableGrant = [
  "[EDCA] Small Business Recovery Grant Application",
  "[EDCA] Small Business Recovery Grant Application - Round 2",
  "SBSL",
  "MSBSG",
  "MSBR Restart Grant",
  "MSBR",
  "EIDL Grant",
  "MEDC Grant",
  "PPP Grant",
  "Saving Business Saving Lives Grant",
  "Stabilization Grant",
];

const grantVsDefination: { [key: string]: string } = {
  "PPP Grant":
    "Paycheck Protection Program – SBA grant to keep people employed during COVID",
  "EIDL Grant": "Economic Injury Disaster Loan – SBA Loan during COVID",
  SBSL: "Saving Business Saving Lives",
  SBSG: "Small Business Stabilization Grant",
  "MEDC Grant": "Pure Michigan Small Business Relief Initiative from MEDC",
  MSBR: "Michigan Small Business Recovery Grant",
  PPE: "Personal Protection Equipment Resilience Grant",
  "MSBR Restart Grant":
    "Michigan Small Business Restart Grant from State of Michigan",
  MSBSG: "Michigan Small Business Survival Grant from State of Michigan",
  RRG: "Restaurant Relief Grant from Oakland County",
};

const emailIssueReasonVsLabel: { [key: string]: string } = {
  invalid_format: " (invalid format)",
  rejected_by_sendgrid: " (rejected by email provider)",
  bounced: " (bounced — address undeliverable)",
};

interface Contact {
  Contact: string;
  Email: string;
  isDefault: boolean;
  receiveBroadcast: boolean;
}

interface ErrorProps {
  message: string;
}

const Error: React.FC<ErrorProps> = ({ message }) => {
  return (
    <>
      <div className="text-sm text-red-600">*{message}*</div>
    </>
  );
};

interface EditFirmProps {
  setOpen: (open: boolean) => void;
  firm: any;
  newFirm: boolean;
  dataFlag: boolean;
  setDataFlag: (flag: boolean) => void;
}

const EditFirm: React.FC<EditFirmProps> = ({
  setOpen,
  firm,
  newFirm,
  dataFlag,
  setDataFlag,
}) => {
  const navigate = useNavigate();
  const [editingFirm, setEditingFirm] = useState<any>(firm);
  const [editingGrant, setEditingGrant] = useState<any>(
    firm.grants ? firm.grants : {}
  );
  const [contacts, setContacts] = useState<Contact[]>([]);

  const updateAndSubmit = (firm: any, grants: any) => {
    if (!validateForm(firm)) {
      console.log("Form validation failed");
      return;
    }
    let defaultContact = contacts.find((contact) => contact.isDefault);
    const additionalContacts = contacts.filter((contact) => !contact.isDefault);
    if (!defaultContact && additionalContacts.length > 0) {
      defaultContact = additionalContacts[0];
      additionalContacts.shift();
    }
    let postData = {
      editedFirm: {
        ...firm,
        grants: grants,
        Contact: defaultContact?.Contact,
        Email: defaultContact?.Email,
        additionalContacts: additionalContacts,
      },
    };
    axiosInstance.post(`/firms`, postData).then((res) => {
      if (res.status === 200) {
        setOpen(false);
        setDataFlag(!dataFlag);
        if (newFirm) {
          const caseId = res.data.result.caseId;
          navigate(`/case?id=${caseId}`);
        }
      } else {
        console.log("API Error");
      }
    });
  };

  const addNewContact = () => {
    setContacts([...contacts, { Contact: "", Email: "", isDefault: false, receiveBroadcast: false }]);
  };

  const handleContactChange = (index: number, value: string) => {
    const updatedContacts = [...contacts];
    updatedContacts[index]["Contact"] = value;
    setContacts(updatedContacts);
  };

  const handleEmailChange = (index: number, value: string) => {
    const updatedContacts = [...contacts];
    updatedContacts[index]["Email"] = value;
    setContacts(updatedContacts);
  };

  const deleteContact = (index: number) => {
    const updatedContacts = [...contacts];
    const isDefaultContact = updatedContacts[index].isDefault;

    const result = updatedContacts.splice(index, 1);

    if (isDefaultContact && updatedContacts.length > 0) {
      updatedContacts[0].isDefault = true;
    }
    setContacts(updatedContacts);
  };

  const handleSetDefaultContact = (index: number) => {
    const updatedContacts = [...contacts];
    updatedContacts.forEach((contact) => {
      contact.isDefault = false;
    });
    updatedContacts[index].isDefault = true;
    updatedContacts[index].receiveBroadcast = true;
    setContacts(updatedContacts);
    // setEditingFirm({...editingFirm, Contact: updatedContacts[index].Contact, Email: updatedContacts[index].Email})
  };

  const handleReceiveBroadcastChange = (index: number, value: boolean) => {
    const updatedContacts = [...contacts];
    updatedContacts[index].receiveBroadcast = value;
    setContacts(updatedContacts);
  };

  useEffect(() => {
    if (editingFirm.additionalContacts) {
      const normalized = editingFirm.additionalContacts.map((c: any) => ({
        ...c,
        receiveBroadcast: c.receiveBroadcast ?? c.isDefault,
      }));
      setContacts(normalized);
    } else {
      setContacts([{ Contact: "", Email: "", isDefault: true, receiveBroadcast: true }]);
    }
  }, []);

  const [missingFields, setMissingFields] = useState<string[]>([]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const contactSectionRef = useRef<HTMLSpanElement>(null);
  const companySectionRef = useRef<HTMLSpanElement>(null);
  const companySizeSectionRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (missingFields.length === 0) return;
    let target: React.RefObject<HTMLElement> | null = null;
    if (missingFields.includes("Contacts")) {
      target = contactSectionRef;
    } else if (
      missingFields.some((f) =>
        ["Company Name", "Address", "ZIP", "City", "Phone", "Email"].includes(f)
      )
    ) {
      target = companySectionRef;
    } else if (
      missingFields.some((f) => ["Employees", "primaryIndustry"].includes(f))
    ) {
      target = companySizeSectionRef;
    }
    if (target?.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const el = target.current;
      container.scrollTop = el.offsetTop - container.offsetTop - 8;
    }
  }, [missingFields]);

  const validateForm = (firm: any): boolean => {
    const errorFields: string[] = [];
    //Validate Company Name
    if (!firm["Company Name"]) {
      errorFields.push("Company Name");
    }
    if (!firm["Address"]) {
      errorFields.push("Address");
    }
    if (!firm["ZIP"]) {
      errorFields.push("ZIP");
    }
    if (!firm["City"]) {
      errorFields.push("City");
    }
    if (!firm["Employees"]) {
      errorFields.push("Employees");
    }
    if (!firm["primaryIndustry"]) {
      errorFields.push("primaryIndustry");
    }

    //Validate Contact
    let defaultContact = contacts.find((contact) => contact.isDefault);
    if (defaultContact) {
      if (!defaultContact.Contact || !defaultContact.Email) {
        errorFields.push("Contacts");
      }
    }
    //Validate Phone Number
    if (!firm["Phone"]) {
      errorFields.push("Phone");
    }
    //Validate Email Address
    if (!firm["Email"]) {
      errorFields.push("Email");
    }
    //Validate County
    // if (!firm["County"]) {
    //   errorFields.push("County");
    // }

    setMissingFields(errorFields);
    return errorFields.length === 0;
  };

  return (
    <>
      <Box
        sx={{
          textAlign: "center",
          backgroundColor: "#9E1F63FF",
          padding: "3px",
          margin: 0,
          color: "white",
          borderRadius: "5px 5px 0 0",
        }}
      >
        <Typography sx={{ display: "inline-block" }} variant="h4">
          {newFirm ? <>Add New Firm</> : <>Edit Firm Data</>}
        </Typography>
        <Box
          sx={{ display: "inline", float: "right", cursor: "pointer" }}
          onClick={() => {
            setOpen(false);
          }}
        >
          <CloseIcon sx={{ fontSize: 40 }}></CloseIcon>
        </Box>
      </Box>
      <Box ref={scrollContainerRef} sx={{ overflowY: "scroll", height: 610 }}>
        <Grid
          container
          rowSpacing={0}
          columnSpacing={{ xs: 1, sm: 2, md: 3 }}
          style={{
            width: "100%",
            margin: "auto",
            marginBottom: "10px",
            paddingRight: "10px",
          }}
        >
          <Grid item xs={12} md={8} style={{ position: "relative" }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent={"space-between"}
            >
              <Typography variant="h6"><span ref={contactSectionRef}>Contact Information</span></Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={editingFirm["do_not_contact"] === "Yes"}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          do_not_contact: "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          do_not_contact: "No",
                        });
                      }
                    }}
                  />
                }
                label="Do Not Contact"
              />
            </Box>
            <hr style={{ marginBottom: "10px" }}></hr>
            {editingFirm["email_issues"]?.length > 0 && (
              <Alert
                severity="warning"
                sx={{
                  width: { xs: "100%", md: "98%" },
                  marginLeft: "1%",
                  marginBottom: "10px",
                }}
              >
                {editingFirm["email_issues"].map((issue: any, index: number) => (
                  <div key={index}>
                    {issue.email} — email delivery problem
                    {emailIssueReasonVsLabel[issue.reason] ?? ""}
                  </div>
                ))}
              </Alert>
            )}
            <div key={contacts.length}>
              {contacts.map((contact, index) => (
                <div key={index} className="flex items-center">
                  <TextField
                    sx={{
                      width: { xs: "100%", md: "48%" },
                      marginLeft: "1%",
                      marginBottom: "2px",
                    }}
                    label="Contact"
                    variant="standard"
                    defaultValue={contact.Contact}
                    onChange={(e) => {
                      handleContactChange(index, e.target.value);
                    }}
                  />
                  <TextField
                    sx={{
                      width: { xs: "100%", md: "48%" },
                      marginLeft: "1%",
                      marginBottom: "2px",
                    }}
                    label="Email"
                    variant="standard"
                    defaultValue={contact.Email}
                    onChange={(e) => {
                      handleEmailChange(index, e.target.value);
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Radio
                        checked={contact.isDefault}
                        onChange={() => handleSetDefaultContact(index)}
                      />
                    }
                    label="Primary"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={contact.receiveBroadcast}
                        onChange={(e) =>
                          handleReceiveBroadcastChange(index, e.target.checked)
                        }
                        disabled={contact.isDefault}
                      />
                    }
                    label="Receives emails"
                  />
                  {contacts.length > 1 && (
                    <IconButton
                      aria-label="Delete"
                      onClick={() => deleteContact(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </div>
              ))}
            </div>
            {missingFields.includes("Contacts") ? (
              <Error message="Primary Contact's Name and Email is Required" />
            ) : (
              <></>
            )}
            <Button onClick={addNewContact}>Add New Contact</Button>

            <Typography variant="h6" sx={{ marginTop: "20px" }}>
              <span ref={companySectionRef}>Company Information</span>
            </Typography>
            <hr style={{ marginBottom: "10px" }}></hr>
            <div className="flex">
              <TextField
                sx={{
                  width: { xs: "100%", md: "48%" },
                  marginLeft: "1%",
                  marginBottom: "2px",
                }}
                label="Company Name"
                variant="standard"
                defaultValue={editingFirm["Company Name"]}
                onChange={(e) => {
                  setEditingFirm({
                    ...editingFirm,
                    "Company Name": e.target.value.trim(),
                  });
                }}
              />
              <InfoTip tipClassName="z-10" iconFontSize="20px">
                Use Contact name if Company Name is not determined
              </InfoTip>
            </div>
            {missingFields.length > 0 &&
              missingFields.includes("Company Name") ? (
              <Error message="Company Name is Required" />
            ) : (
              <></>
            )}
            <TextField
              sx={{
                width: { xs: "100%", md: "98%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              label="Address 1"
              variant="standard"
              defaultValue={editingFirm["Address"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  Address: e.target.value,
                });
              }}
            />
            {missingFields.length > 0 && missingFields.includes("Address") ? (
              <Error message="Address is Required" />
            ) : (
              <></>
            )}
            <TextField
              sx={{
                width: { xs: "100%", md: "98%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              label="Address 2"
              variant="standard"
              defaultValue={editingFirm["Address 2"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  "Address 2": e.target.value,
                });
              }}
            />
            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              label="Zip"
              variant="standard"
              defaultValue={editingFirm["ZIP"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  ZIP: e.target.value,
                });
              }}
            />
            {missingFields.length > 0 && missingFields.includes("ZIP") ? (
              <Error message="Zipcode is Required" />
            ) : (
              <></>
            )}
            <Autocomplete
              options={cities}
              value={editingFirm["City"] || null}
              onChange={(event, newValue) => {
                setEditingFirm({
                  ...editingFirm,
                  City: newValue || "",
                });
              }}
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="City"
                  variant="standard"
                />
              )}
            />
            {missingFields.length > 0 && missingFields.includes("City") ? (
              <Error message="City is Required" />
            ) : (
              <></>
            )}
            <TextField
              sx={{
                width: { xs: "100%", md: "24%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              label="State"
              variant="standard"
              defaultValue={editingFirm["State"] ? editingFirm["State"] : "MI"}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  State: e.target.value,
                });
              }}
            />
            <TextField
              sx={{
                width: { xs: "100%", md: "24%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              label="County"
              variant="standard"
              defaultValue={
                editingFirm["County"] ? editingFirm["County"] : "Oakland"
              }
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  County: e.target.value,
                });
              }}
            />
            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              label="Phone"
              variant="standard"
              defaultValue={editingFirm["Phone"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  Phone: e.target.value,
                });
              }}
            />
            {missingFields.length > 0 && missingFields.includes("Phone") ? (
              <Error message="Phone Number is required" />
            ) : (
              <></>
            )}
            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              label="Company Email"
              variant="standard"
              defaultValue={editingFirm["Email"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  Email: e.target.value,
                });
              }}
            />
            {missingFields.length > 0 && missingFields.includes("Email") ? (
              <Error message="Company Email is required" />
            ) : (
              <></>
            )}



            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              label="Website"
              variant="standard"
              defaultValue={editingFirm["Website"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  Website: e.target.value,
                });
              }}
            />
            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              label="Founding Year"
              variant="standard"
              defaultValue={editingFirm["Founding Year"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  "Founding Year": e.target.value,
                });
              }}
            />
            <Typography variant="h6" sx={{ marginTop: "20px" }}>
              <span ref={companySizeSectionRef}>Company Size</span>
            </Typography>
            <hr style={{ marginBottom: "10px" }}></hr>
            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              type="number"
              label="Current Employees"
              variant="standard"
              defaultValue={editingFirm["Employees"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  Employees: e.target.value,
                });
              }}
            />
            {missingFields.length > 0 && missingFields.includes("Employees") ? (
              <Error message="Employees is Required" />
            ) : (
              <></>
            )}
            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              type="number"
              label="Current Annual Revenue"
              variant="standard"
              defaultValue={editingFirm["Annual Revenue"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  "Annual Revenue": e.target.value,
                });
              }}
            />

            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              type="number"
              label="2025 Employees"
              variant="standard"
              defaultValue={editingFirm["Employees 2025"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  "Employees 2025": e.target.value,
                });
              }}
            />
            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              type="number"
              label="2025 Revenue"
              variant="standard"
              defaultValue={editingFirm["Annual Revenue 2025"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  "Annual Revenue 2025": e.target.value,
                });
              }}
            />
            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              type="number"
              label="2024 Employees"
              variant="standard"
              defaultValue={editingFirm["Employees 2024"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  "Employees 2024": e.target.value,
                });
              }}
            />
            <TextField
              sx={{
                width: { xs: "100%", md: "48%" },
                marginLeft: "1%",
                marginBottom: "2px",
              }}
              type="number"
              label="2024 Revenue"
              variant="standard"
              defaultValue={editingFirm["Annual Revenue 2024"]}
              onChange={(e) => {
                setEditingFirm({
                  ...editingFirm,
                  "Annual Revenue 2024": e.target.value,
                });
              }}
            />

            <Typography variant="h6" sx={{ marginTop: "20px" }}>
              Company Industry
            </Typography>
            <hr style={{ marginBottom: "10px" }}></hr>
            <FormControl fullWidth sx={{ marginTop: "10px" }}>
              <InputLabel>Primary Industry</InputLabel>
              <Select
                value={editingFirm["primaryIndustry"]}
                label="Primary Industry"
                onChange={(e) => {
                  setEditingFirm({
                    ...editingFirm,
                    primaryIndustry: e.target.value,
                  });
                }}
              >
                <MenuItem
                  value={
                    "Accommodations - Bed and breakfast, or any other lodging facility"
                  }
                >
                  Accommodations - Bed and breakfast, or any other lodging
                  facility
                </MenuItem>
                <MenuItem value={"Business Services"}>
                  Business Services
                </MenuItem>
                <MenuItem
                  value={
                    "Construction and Maintenance Services - Grading, excavating, other construction and maintenance services"
                  }
                >
                  Construction and Maintenance Services - Grading, excavating,
                  other construction and maintenance services
                </MenuItem>
                <MenuItem value={"Engineering, Design and Development"}>
                  Engineering, Design and Development
                </MenuItem>
                <MenuItem value={"Entertainment and Recreation"}>
                  Entertainment and Recreation
                </MenuItem>
                <MenuItem value={"Food"}>Food</MenuItem>
                <MenuItem
                  value={"Healthcare - chiropractors, dental, health clinics"}
                >
                  Healthcare - chiropractors, dental, health clinics
                </MenuItem>
                <MenuItem
                  value={
                    "Maintenance & Repair - automobile repair, automobile dealership, cellular phone repair, landscaping and lawn maintenance"
                  }
                >
                  Maintenance & Repair - automobile repair, automobile
                  dealership, cellular phone repair, landscaping and lawn
                  maintenance
                </MenuItem>
                <MenuItem
                  value={
                    "Nonprofit - churches, religious centers, advocacy organizations, etc."
                  }
                >
                  Nonprofit - churches, religious centers, advocacy
                  organizations, etc.
                </MenuItem>
                <MenuItem value={"Other"}>Other</MenuItem>
                <MenuItem value={"Manufacturing"}>Manufacturing</MenuItem>
                <MenuItem value={"Personal Services"}>
                  Personal Services
                </MenuItem>
                <MenuItem
                  value={
                    "Professional & Other Services - taxes, consulting, publishing, marketing"
                  }
                >
                  Professional & Other Services - taxes, consulting, publishing,
                  marketing
                </MenuItem>
                <MenuItem value={"Retail"}>Retail</MenuItem>
                <MenuItem value={"Technology"}>Technology</MenuItem>
                <MenuItem value={"Transportation - limousine services, etc."}>
                  Transportation - limousine services, etc.
                </MenuItem>
              </Select>
            </FormControl>
            {missingFields.length > 0 &&
              missingFields.includes("primaryIndustry") ? (
              <Error message="Primary Industry is Required" />
            ) : (
              <></>
            )}
            <FormControl fullWidth sx={{ marginTop: "10px" }}>
              <InputLabel>Secondary Industry</InputLabel>
              <Select
                label="Secondary Industry"
                value={editingFirm["secondaryIndustry"]}
                onChange={(e) => {
                  setEditingFirm({
                    ...editingFirm,
                    secondaryIndustry: e.target.value,
                  });
                }}
              >
                <MenuItem value={""}>No Secondary Industry Category</MenuItem>
                <MenuItem
                  value={
                    "Business Services - Landscaping, extermination, janitorial and housekeeping services"
                  }
                >
                  Business Services - Landscaping, extermination, janitorial and
                  housekeeping services
                </MenuItem>
                <MenuItem
                  value={
                    "Business Services - Printing, market research, photography, advertising, public relations and related services"
                  }
                >
                  Business Services - Printing, market research, photography,
                  advertising, public relations and related services
                </MenuItem>
                <MenuItem
                  value={
                    "Business Services - Computer systems design, software, programming services"
                  }
                >
                  Business Services - Computer systems design, software,
                  programming services
                </MenuItem>
                <MenuItem
                  value={
                    "Business Services - Interior, industrial, graphic, and other specialized design services"
                  }
                >
                  Business Services - Interior, industrial, graphic, and other
                  specialized design services
                </MenuItem>
                <MenuItem
                  value={
                    "Entertainment and Recreation - Golf courses, skiing facilities, other recreation industries"
                  }
                >
                  Entertainment and Recreation - Golf courses, skiing
                  facilities, other recreation industries
                </MenuItem>
                <MenuItem
                  value={
                    "Entertainment and Recreation - Yoga studios, gyms, fitness studios, personal trainers"
                  }
                >
                  Entertainment and Recreation - Yoga studios, gyms, fitness
                  studios, personal trainers
                </MenuItem>
                <MenuItem value={"Personal Services - Childcare"}>
                  Personal Services - Childcare
                </MenuItem>
                <MenuItem
                  value={
                    "Personal Services - Barber shops, hair and nail salons"
                  }
                >
                  Personal Services - Barber shops, hair and nail salons
                </MenuItem>
                <MenuItem
                  value={
                    "Personal Services - Diet, weight loss, spas, massage, other personal care services"
                  }
                >
                  Personal Services - Diet, weight loss, spas, massage, other
                  personal care services
                </MenuItem>
                <MenuItem
                  value={
                    "Retail - Bookstores, hardware, anything sold that is not food"
                  }
                >
                  Retail - Bookstores, hardware, anything sold that is not food
                </MenuItem>
                <MenuItem
                  value={
                    "Retail - Clothing, shoe, sporting goods, resale and consignment stores"
                  }
                >
                  Retail - Clothing, shoe, sporting goods, resale and
                  consignment stores
                </MenuItem>
                <MenuItem
                  value={
                    "Retail - Jewelry, art, hobby, musical instrument, pet supply and other miscellaneous retail stores"
                  }
                >
                  Retail - Jewelry, art, hobby, musical instrument, pet supply
                  and other miscellaneous retail stores
                </MenuItem>
                <MenuItem
                  value={
                    "Retail - Pharmacies, cosmetics, optical goods, other health and personal care stores"
                  }
                >
                  Retail - Pharmacies, cosmetics, optical goods, other health
                  and personal care stores
                </MenuItem>
                <MenuItem value={"Retail - Groceries"}>
                  Retail - Groceries
                </MenuItem>
              </Select>
            </FormControl>
            <Typography variant="h6">Grants Applications</Typography>
            <hr></hr>
            <TableContainer
              component={"table"}
              sx={{ backgroundColor: "white" }}
            >
              <TableHead>
                <StyledTableRow>
                  <StyledTableCell></StyledTableCell>
                  <StyledTableCell>Grant</StyledTableCell>
                  <StyledTableCell>Approved?</StyledTableCell>
                  <StyledTableCell align="right">Amount</StyledTableCell>
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {availableGrant.map((grant: any, index: number) => (
                  <StyledTableRow key={index}>
                    <StyledTableCell>
                      <Checkbox
                        checked={editingGrant?.[grant]?.["applied"]}
                        onChange={(e) => {
                          setEditingGrant({
                            ...editingGrant,
                            [grant]: {
                              ...editingGrant[grant],
                              applied: e.target.checked,
                            },
                          });
                        }}
                      ></Checkbox>
                    </StyledTableCell>
                    <StyledTableCell>
                      <div className="flex items-center">
                        {grant}
                        {grantVsDefination[grant] && (
                          <InfoTip
                            className="ml-1"
                            tipClassName="z-10"
                            iconFontSize="20px"
                          >
                            {grantVsDefination[grant]}
                          </InfoTip>
                        )}
                      </div>
                    </StyledTableCell>
                    <StyledTableCell align="center">
                      <Checkbox
                        checked={
                          editingGrant?.[grant]?.["decision"] === "Approved"
                        }
                        onChange={(e) => {
                          setEditingGrant({
                            ...editingGrant,
                            [grant]: {
                              ...editingGrant[grant],
                              decision: e.target.checked
                                ? "Approved"
                                : "Rejected",
                            },
                          });
                        }}
                      ></Checkbox>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Input
                        sx={{ width: "70%", float: "right" }}
                        value={editingGrant?.[grant]?.["value"]}
                        onChange={(e) => {
                          setEditingGrant({
                            ...editingGrant,
                            [grant]: {
                              ...editingGrant[grant],
                              value: e.target.value,
                            },
                          });
                        }}
                      ></Input>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </TableContainer>
            <StyledTextarea
              sx={{ width: { xs: "300px", md: "600px" } }}
              minRows={8}
              maxRows={10}
              placeholder="Firm Notes"
              value={editingFirm["notes"]}
              onChange={(e: any) => {
                setEditingFirm({
                  ...editingFirm,
                  notes: e.target.value,
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={4} style={{ position: "relative" }}>
            <FormGroup>
              <Typography variant="h6">Classifications</Typography>
              <hr></hr>
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(editingFirm["Client"])}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          Client: "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          Client: "No",
                        });
                      }
                    }}
                  />
                }
                label="Client"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Woman-Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Woman-Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Woman-Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Woman-Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Minority-Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Minority-Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Minority-Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Minority-Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Veteran-Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Veteran-Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Veteran-Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Veteran-Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Disabled-Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Disabled-Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Disabled-Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Disabled-Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["LGBTQ+ Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "LGBTQ+ Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "LGBTQ+ Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="LGBTQ+ Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Youth Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Youth Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Youth Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Youth Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Low to Moderate Income Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Low to Moderate Income Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Low to Moderate Income Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Low to Moderate Income Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Non-English Business"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Non-English Business": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Non-English Business": "No",
                        });
                      }
                    }}
                  />
                }
                label="Non-English Business"
              />
              {/* NEP toggle hidden — replaced by GRP below. Kept for reference.
              <div className="flex items-center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={["Yes", "=Yes"].includes(editingFirm["NEP"])}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditingFirm({
                            ...editingFirm,
                            NEP: "Yes",
                          });
                        } else {
                          setEditingFirm({
                            ...editingFirm,
                            NEP: "No",
                          });
                        }
                      }}
                    />
                  }
                  label="NEP"
                />
                <InfoTip tipClassName="z-10" iconFontSize="20px">
                  Newcomer Entrepreneur Program
                </InfoTip>
              </div>
              */}
              <div className="flex items-center">
                <FormControlLabel
                  control={
                    <Switch
                      checked={["Yes", "=Yes"].includes(editingFirm["GRP"])}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditingFirm({
                            ...editingFirm,
                            GRP: "Yes",
                          });
                        } else {
                          setEditingFirm({
                            ...editingFirm,
                            GRP: "No",
                          });
                        }
                      }}
                    />
                  }
                  label="GRP"
                />
                <InfoTip tipClassName="z-10" iconFontSize="20px">
                  Global Resource Program
                </InfoTip>
              </div>
              <Typography variant="h6">Certifications</Typography>
              <hr></hr>
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Certified Women Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Certified Women Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Certified Women Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Certified Women Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Certified Minority Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Certified Minority Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Certified Minority Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Certified Minority Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Certified Disabled Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Certified Disabled Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Certified Disabled Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Certified Disabled Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Certified Veteran Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Certified Veteran Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Certified Veteran Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Certified Veteran Owned"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Certified LGBTQ+ Owned"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Certified LGBTQ+ Owned": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Certified LGBTQ+ Owned": "No",
                        });
                      }
                    }}
                  />
                }
                label="Certified LGBTQ+ Owned"
              />
              <Typography variant="h6">Active Business Channels</Typography>
              <hr></hr>
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Home-based"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Home-based": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Home-based": "No",
                        });
                      }
                    }}
                  />
                }
                label="Home-based"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Selling Online"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Selling Online": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Selling Online": "No",
                        });
                      }
                    }}
                  />
                }
                label="Selling Online"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["In-Person Sales"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "In-Person Sales": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "In-Person Sales": "No",
                        });
                      }
                    }}
                  />
                }
                label="In-Person Sales"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Commercial Location"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Commercial Location": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Commercial Location": "No",
                        });
                      }
                    }}
                  />
                }
                label="Commercial Location"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={["Yes", "=Yes"].includes(
                      editingFirm["Mobile Business"]
                    )}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setEditingFirm({
                          ...editingFirm,
                          "Mobile Business": "Yes",
                        });
                      } else {
                        setEditingFirm({
                          ...editingFirm,
                          "Mobile Business": "No",
                        });
                      }
                    }}
                  />
                }
                label="Mobile Business"
              />
            </FormGroup>
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          bottom: 10,
          width: "100%",
          textAlign: "center",
          position: "fixed",
        }}
      >
        <Button
          variant="contained"
          sx={{
            width: "200px",
            fontSize: "20px",
            backgroundColor: "#9E1F63FF",
          }}
          onClick={() => {
            updateAndSubmit(editingFirm, editingGrant);
          }}
        >
          Save
        </Button>
      </Box>
    </>
  );
};

export default EditFirm;
